import moment from 'moment';
import { nullSafeGet, nullSafeGetOrElse } from './DataAccessUtils';
import {
	PROJECT_TEMPLATE_FIELD_GROUPS,
	PROJECT_TEMPLATE_FIELD_TYPES,
} from '../constants/ProjectTemplateFields';

// --- A project's template field values --------------------------------------
// A template's custom fields are rendered dynamically on the project forms, so
// their form names are built from the field key. A string name is a single segment
// in antd v4 (no dot splitting), so a key containing a period is still one field.
export const templateFieldName = (group, key) => `${group}-${key}-valueAsString`;

export const isMultipleChoiceField = (field) =>
	nullSafeGet('type', field) === PROJECT_TEMPLATE_FIELD_TYPES.multipleChoice;

// Multi-select is the "user can pick more than one" variant of multiple choice —
// the same distinction troubleshooting draws with allowMultipleAnswerSelections.
export const isMultiSelectField = (field) =>
	isMultipleChoiceField(field) && !!nullSafeGet('allowMultipleAnswerSelections', field);

// The option strings of a multiple-choice field. Stored as [{ value }] so an option
// can gain a description later without a migration, matching troubleshooting's
// multipleChoices.
export const getFieldChoices = (field) =>
	nullSafeGetOrElse('multipleChoices', field, [])
		.map((choice) => nullSafeGet('value', choice))
		.filter((value) => value !== undefined && value !== null && value !== '');

const isEmptyFieldValue = (value) =>
	value === undefined ||
	value === null ||
	value === '' ||
	(Array.isArray(value) && value.length === 0);

// A project's saved value for one field — the backend FieldTypeDetails.
//
// A multi-select answer lives in `valuesAsStrings`, mirroring TroubleshootingAnswer's
// answer / multipleAnswers split (models/troublehooting/WorkOrderTroubleshootingAttributes.scala):
// one column for the single answer, one for the many. `valueAsString` is a
// non-optional String on the backend, so a multi-select still sends it — as ''.
export const buildTemplateFieldDetail = (field, value) => {
	if (isMultiSelectField(field)) {
		const values = Array.isArray(value) ? value : isEmptyFieldValue(value) ? [] : [value];
		return { key: field.key, valueType: field.type, valueAsString: '', valuesAsStrings: values };
	}
	return {
		key: field.key,
		valueType: field.type,
		valueAsString: value === undefined || value === null ? '' : `${value}`,
	};
};

// The saved detail back into a form value.
//
// `valuesAsStrings` is authoritative for a multi-select. The fallback to
// `valueAsString` is not defensive padding: a field that was single-choice or text when
// its value was saved has its answer there, and so does anything saved by a client
// talking to a backend that predates the column — decoding it means switching a field
// to multi-select doesn't blank out what projects already hold.
export const parseTemplateFieldValue = (field, detail) => {
	if (!isMultiSelectField(field)) {
		// A field switched back from multi-select still has its answers in valuesAsStrings,
		// with valueAsString deliberately empty — reading only the scalar would show every
		// such project as unset. Joined rather than truncated: dropping the extras would
		// quietly lose answers the user did give.
		const valueAsString = nullSafeGet('valueAsString', detail);
		if (valueAsString !== undefined && valueAsString !== null && valueAsString !== '') {
			return valueAsString;
		}
		const values = nullSafeGet('valuesAsStrings', detail);
		return Array.isArray(values) && values.length > 0 ? values.join(', ') : valueAsString;
	}
	const values = nullSafeGet('valuesAsStrings', detail);
	if (Array.isArray(values)) {
		return values;
	}
	const valueAsString = nullSafeGet('valueAsString', detail);
	if (isEmptyFieldValue(valueAsString)) {
		return [];
	}
	const text = `${valueAsString}`;
	if (text.charAt(0) === '[') {
		try {
			const parsed = JSON.parse(text);
			if (Array.isArray(parsed)) {
				return parsed;
			}
		} catch (error) {
			// Not JSON after all — fall through to the comma split below.
		}
	}
	return text
		.split(',')
		.map((option) => option.trim())
		.filter((option) => option !== '');
};

// A field's saved value as display text. Returns '' when there is nothing saved so the
// caller decides how an unset field reads — never invent a value here.
export const formatTemplateFieldValue = (field, detail) => {
	const value = parseTemplateFieldValue(field, detail);
	return Array.isArray(value) ? value.join(', ') : isEmptyFieldValue(value) ? '' : `${value}`;
};

// Saved details keyed by field key. A plain object indexed with brackets, never a
// nullSafeGet path — a field key can contain a period, which a dotted path would split.
const getDetailsByKey = (projectTemplateDetails) => {
	const savedByKey = {};
	(projectTemplateDetails || []).forEach((detail) => {
		savedByKey[detail.key] = detail;
	});
	return savedByKey;
};

// { key, valueType, valueAsString } per template field, read back out of the form.
// Required fields always go; optional ones only when filled in. The key and type
// come from the template itself rather than from hidden mirror fields.
export const getProjectTemplateDetails = (projectTemplate, values) => {
	const detailsFor = (group, keepEmpty) =>
		nullSafeGetOrElse(group, projectTemplate, [])
			.map((field) => ({ field, value: values[templateFieldName(group, field.key)] }))
			.filter(({ value }) => keepEmpty || !isEmptyFieldValue(value))
			.map(({ field, value }) => buildTemplateFieldDetail(field, value));
	return detailsFor('requiredFields', true).concat(detailsFor('optionalFields', false));
};

// The saved values keyed by field key, for seeding the inputs once a template's
// fields exist. They mount only after a template is picked/fetched, so they can't
// ride along in the form's initialValues.
export const getTemplateDetailPrefill = (projectTemplate, projectTemplateDetails) => {
	const savedByKey = getDetailsByKey(projectTemplateDetails);
	const prefill = {};
	PROJECT_TEMPLATE_FIELD_GROUPS.forEach((group) => {
		nullSafeGetOrElse(group, projectTemplate, []).forEach((field) => {
			if (savedByKey[field.key] !== undefined) {
				prefill[templateFieldName(group, field.key)] = parseTemplateFieldValue(
					field,
					savedByKey[field.key]
				);
			}
		});
	});
	return prefill;
};

// Every field the template defines, paired with what the project saved against it, in
// authoring order (required first). Used by the read-only display and by the modal that
// edits those values, so both agree on ordering and on which group a field belongs to.
export const getProjectTemplateFieldEntries = (projectTemplate, projectTemplateDetails) => {
	const savedByKey = getDetailsByKey(projectTemplateDetails);
	return PROJECT_TEMPLATE_FIELD_GROUPS.reduce(
		(entries, group) =>
			entries.concat(
				nullSafeGetOrElse(group, projectTemplate, []).map((field) => ({
					group,
					field,
					isRequired: group === 'requiredFields',
					detail: savedByKey[field.key],
					displayValue: formatTemplateFieldValue(field, savedByKey[field.key]),
				}))
			),
		[]
	);
};

// --- Template item scheduling ------------------------------------------------
// A template item's "time period" is how many days of work it takes, not a date of its
// own: a template is reused across projects that start on different days.
//
// The count is INCLUSIVE of the first day. An item taking 7 days on a project starting
// October 1 is due October 7 — it occupies the 1st through the 7th. On a project that
// has only an end date the same reading runs backwards: 7 days against an October 31
// end is due October 25, occupying the 25th through the 31st.
//
// A dependent deliverable's days count from the deliverable it waits on instead of from
// the project (see getTemplateDeliverableDueDates), and there the parent's due date is
// already the day before this one starts — so that case adds the full number of days
// with no adjustment. 7-day D1 from October 1 is due the 7th; a 14-day D2 depending on
// it is due the 21st, occupying the 8th through the 21st.
//
// Returns a moment, or undefined when the item has no period or the project has
// neither date — an absent due date is left absent rather than guessed at.
export const getTemplateItemDueDate = (daysForDue, startDate, endDate) => {
	if (daysForDue === undefined || daysForDue === null || daysForDue === '') {
		return undefined;
	}
	const days = Number(daysForDue);
	if (!isFinite(days)) {
		return undefined;
	}
	// Inclusive of the day the work starts: an item taking 7 days from an October 1 start
	// is due October 7, occupying the 1st through the 7th — not October 8. Hence days - 1.
	//
	// Floored at one day so a 0 lands on the project's own date rather than the day before
	// it; a zero-day item is not a thing, and older templates may carry one.
	const inclusiveOffset = Math.max(days, 1) - 1;
	if (startDate) {
		return moment(startDate).add(inclusiveOffset, 'days');
	}
	if (endDate) {
		// Same reading from the other end: 7 days on a project ending October 31 occupies
		// the 25th through the 31st.
		return moment(endDate).subtract(inclusiveOffset, 'days');
	}
	return undefined;
};

// How a template item's period reads on a project with the given dates, e.g.
// "5 days after start · Mar 6, 2026". Empty string when there is no period to describe.
export const getTemplateItemDueDateHint = (daysForDue, startDate, endDate) => {
	const dueDate = getTemplateItemDueDate(daysForDue, startDate, endDate);
	if (!dueDate) {
		return '';
	}
	return `${daysForDue} ${Number(daysForDue) === 1 ? 'day' : 'days'} ${
		startDate ? 'from start' : 'up to the end'
	} · ${dueDate.format('MMM D, YYYY')}`;
};

// "Copy and create new" for a project template — same flow as an asset copy
// (getAssetCopy in assetUtils.ts): the source record becomes the form's initial
// values with its identity and audit trail stripped, so saving creates a new
// template rather than editing the source.
//
// Template items (deliverable templates with their nested tasks, and top-level
// task templates) are content on the template and carry no ids of their own, so
// they copy across wholesale — that's the point of the flow.
export const getProjectTemplateCopy = (projectTemplate) => ({
	...projectTemplate,
	id: undefined,
	name: `Copy of ${nullSafeGetOrElse('name', projectTemplate, '')}`,
	requiredFields: nullSafeGetOrElse('requiredFields', projectTemplate, []),
	optionalFields: nullSafeGetOrElse('optionalFields', projectTemplate, []),
	deliverableTemplates: nullSafeGetOrElse('deliverableTemplates', projectTemplate, []),
	taskTemplates: nullSafeGetOrElse('taskTemplates', projectTemplate, []),
	// Audit fields belong to the source record; the backend stamps its own on create.
	createdBy: undefined,
	createdByContact: undefined,
	createdAt: undefined,
	updatedBy: undefined,
	updatedByContact: undefined,
	updatedAt: undefined,
	isDeleted: undefined,
	deletedAt: undefined,
});

// --- Template deliverable dependencies ---------------------------------------
// A template deliverable references the one it waits on by that row's stable `id`
// (see ProjectTemplateDeliverable.id), never by array position — position changes on
// add, remove and reorder, and a dependency that silently re-points at a different
// deliverable is worse than none, since the cascade would then move the wrong dates.

// Rows keyed by id, for resolving a dependsOnId. Bracket access, not a nullSafeGet
// path: an id is opaque and a dotted path would split on any period in it.
const getDeliverablesById = (deliverables) => {
	const byId = {};
	(deliverables || []).forEach((deliverable) => {
		const id = nullSafeGet('id', deliverable);
		if (id) {
			byId[id] = deliverable;
		}
	});
	return byId;
};

// The names of deliverables sitting on a dependency cycle. The deliverable API rejects
// a cycle with a 400, so it is caught while the template is written rather than on some
// project whose author never saw the template.
export const getDependencyCycleNames = (deliverables) => {
	const byId = getDeliverablesById(deliverables);
	const onCycle = [];
	(deliverables || []).forEach((deliverable) => {
		const seen = new Set();
		let current = deliverable;
		while (current) {
			const currentId = nullSafeGet('id', current);
			if (currentId && seen.has(currentId)) {
				onCycle.push(nullSafeGetOrElse('name', deliverable, 'Untitled deliverable'));
				return;
			}
			if (currentId) {
				seen.add(currentId);
			}
			const dependsOnId = nullSafeGet('dependsOnId', current);
			current = dependsOnId ? byId[dependsOnId] : null;
		}
	});
	return onCycle;
};

// A dependent deliverable's days count from the deliverable it waits on, not from the
// project — "10 days to rebuild" means ten days after demolition finishes, not ten days
// after the project starts. Independent ones keep counting from the project's own dates.
//
// Returns a moment per deliverable id. A row whose chain has no resolvable date (no
// period anywhere up it, or the project has neither date) simply gets none.
export const getTemplateDeliverableDueDates = (deliverables, startDate, endDate) => {
	const byId = getDeliverablesById(deliverables);
	const resolved = {};

	// Which rows wait on a given row. Only needed for the backward case, where a
	// deliverable has to finish before the earliest thing that depends on it starts.
	const dependentsOf = {};
	(deliverables || []).forEach((deliverable) => {
		const dependsOnId = nullSafeGet('dependsOnId', deliverable);
		if (dependsOnId) {
			dependentsOf[dependsOnId] = (dependentsOf[dependsOnId] || []).concat([deliverable]);
		}
	});

	const periodOf = (deliverable) => {
		const daysForDue = nullSafeGet('daysForDue', deliverable);
		return daysForDue === undefined || daysForDue === null || daysForDue === ''
			? undefined
			: Number(daysForDue);
	};

	const resolve = (deliverable, seen) => {
		const id = nullSafeGet('id', deliverable);
		if (id && resolved[id] !== undefined) {
			return resolved[id];
		}
		// A cycle is blocked at save time, but a template written before that check — or by
		// another client — must not hang this.
		if (id && seen.has(id)) {
			return undefined;
		}
		if (id) {
			seen.add(id);
		}

		const days = periodOf(deliverable);
		let dueDate;

		if (startDate) {
			// Forward from the project's start. A dependent counts its days from the
			// deliverable it waits on — the parent's date is the day before this one starts,
			// so the full period is added with no adjustment.
			const parentId = nullSafeGet('dependsOnId', deliverable);
			const parent = parentId ? byId[parentId] : null;
			if (parent) {
				const parentDueDate = resolve(parent, seen);
				// No period of its own means no date, the same as a root without one — a
				// deliverable left blank must not inherit its parent's deadline.
				dueDate =
					parentDueDate && days !== undefined ? moment(parentDueDate).add(days, 'days') : undefined;
			} else {
				dueDate = getTemplateItemDueDate(days, startDate, endDate);
			}
		} else {
			// Backward from the project's end. The chain has to finish BY the end, so it is
			// anchored at its last deliverable and walked back up: scheduling a dependent
			// forward from a parent already sitting near the end pushed it past the project.
			const dependents = id ? dependentsOf[id] || [] : [];
			if (dependents.length === 0) {
				dueDate = getTemplateItemDueDate(days, startDate, endDate);
			} else {
				const dependentDates = dependents
					.map((dependent) => resolve(dependent, seen))
					.filter((date) => !!date);
				// This must be finished before the earliest thing waiting on it can start. With
				// no dependent date to work back from — every dependent left without a period —
				// it falls back to the project's end, exactly as a deliverable with no
				// dependents would: a blank child must not erase its parent's own deadline.
				const earliest = dependentDates.length
					? dependentDates.reduce((soonest, date) => (date.isBefore(soonest) ? date : soonest))
					: undefined;
				if (!earliest) {
					dueDate = getTemplateItemDueDate(days, startDate, endDate);
				} else {
					dueDate = days !== undefined ? moment(earliest).subtract(days, 'days') : undefined;
				}
			}
		}

		if (id) {
			resolved[id] = dueDate;
		}
		return dueDate;
	};

	(deliverables || []).forEach((deliverable) => resolve(deliverable, new Set()));
	return resolved;
};

// Pair each created deliverable with the id it must depend on. The created list comes
// back in the order it was sent (the API defaults orderingId to the payload's array
// index), so position n of one is position n of the other — the template's own ids are
// not persisted on the created deliverables.
export const getDeliverableDependencyUpdates = (deliverableTemplates, createdDeliverables) => {
	const indexById = {};
	(deliverableTemplates || []).forEach((deliverable, index) => {
		const id = nullSafeGet('id', deliverable);
		if (id) {
			indexById[id] = index;
		}
	});
	return (deliverableTemplates || [])
		.map((deliverable, index) => {
			const dependsOnId = nullSafeGet('dependsOnId', deliverable);
			const parentIndex = dependsOnId === undefined ? undefined : indexById[dependsOnId];
			const target = parentIndex === undefined ? null : createdDeliverables[parentIndex];
			const dependent = createdDeliverables[index];
			return dependent && target && nullSafeGet('id', target)
				? { deliverable: dependent, dependsOnDeliverableId: nullSafeGet('id', target) }
				: null;
		})
		.filter((update) => update !== null);
};
